import { determineObjectTypes, OperationTypes } from "@/utils/utils";
import { CanvasObjectModel, useRemoteRequests } from "@/hooks/use-remote-requests.";
import { useHandleObjects } from "@/hooks/use-handle-objects";
import { useStore } from "vuex";
import { computed, watch } from "vue";
import { serializeFabricElementWithCustomAttributes } from "@/utils/fabric-utils";
import { CUSTOM_CONTROL_DELETE_ID } from "@/hooks/use-canvas";
import { MODULES, vuexName } from "@/store/utils";

export interface IUseCanvasObjectUpdateCallbacks {
  onClearAll?: () => void;
  onRemoveObject?: (objectId: string) => void;
}

export const useCanvasObjectUpdate = (callbacks?: IUseCanvasObjectUpdateCallbacks) => {
  const { getters } = useStore();
  const { removeObjectFromCanvas, editObjectFromCanvas, addObjectToCanvas, removeAllObjectsFromCanvas } = useHandleObjects({
    onRemoveObject: callbacks?.onRemoveObject,
  });
  const { requestUpdateCanvasObject } = useRemoteRequests();
  const nextObjectTobeUpdated = computed<CanvasObjectModel | undefined>(
    () => getters[vuexName(MODULES.ANNOTATION_V2.GETTERS.GET_NEXT_OBJECT_TOBE_UPDATED)],
  );

  const handlePressRemoveIcon = async (_: any, transform: any) => {
    // "corner": The corner of the object being interacted with
    // Some actions on the object can invoke this function, but we only want to remove the object when the corner is the delete icon
    if (transform?.corner !== CUSTOM_CONTROL_DELETE_ID) return;
    await handleRemoveObjectFromCanvas(transform.target);
  };
  const handleRemoveObjectFromCanvas = async (target: any) => {
    const canvas = target.canvas;
    canvas.remove(target);
    canvas.requestRenderAll();
    const type = determineObjectTypes(target.type);
    await requestUpdateCanvasObject({ id: target.objectId, type, operation: OperationTypes.Remove });
  };

  const handleEditObjectFromCanvas = async (event: any) => {
    const target = event.target;
    const type = determineObjectTypes(target.type);
    await requestUpdateCanvasObject({
      id: target.objectId,
      type,
      operation: OperationTypes.Edit,
      metadata: JSON.stringify(serializeFabricElementWithCustomAttributes(event.target)),
    });
  };

  const handleAddObjectFromCanvas = async (event: any) => {
    const target = event.target;
    const type = determineObjectTypes(target.type);
    await requestUpdateCanvasObject({
      id: target.objectId,
      type,
      operation: OperationTypes.Add,
      metadata: JSON.stringify(serializeFabricElementWithCustomAttributes(event.target)),
    });
  };

  const handleRemoveAllObjectsFromCanvas = async () => {
    await requestUpdateCanvasObject({
      id: "",
      operation: OperationTypes.ClearAll,
      metadata: "",
    });
  };

  watch(nextObjectTobeUpdated, (val) => {
    if (!val) return;

    switch (val.operation) {
      case OperationTypes.Add:
        // Add object to canvas - metadata contains the full fabric object JSON
        if (val.metadata) {
          addObjectToCanvas(val.metadata);
        }
        break;
      case OperationTypes.Edit:
        editObjectFromCanvas(val.id, val.metadata);
        break;
      case OperationTypes.Remove:
        removeObjectFromCanvas(val.id);
        break;
      case OperationTypes.ClearAll:
        removeAllObjectsFromCanvas();
        callbacks?.onClearAll?.();
        break;
    }
  });

  return {
    handlePressRemoveIcon,
    handleEditObjectFromCanvas,
    handleRemoveObjectFromCanvas,
    handleAddObjectFromCanvas,
    handleRemoveAllObjectsFromCanvas,
  };
};
